import React, { useMemo, useState } from "react";
import "./golang.css"; // ← CSS thuần

export default function GolangDeployOnePage() {
  const [serviceName, setServiceName] = useState("testgo");
  const [description, setDescription] = useState("TestGo Server (port 8725)");
  const [user, setUser] = useState("www-data");
  const [group, setGroup] = useState("www-data");
  const [workingDir, setWorkingDir] = useState("/home/caominh/code/testgo");
  const [binaryPath, setBinaryPath] = useState("/usr/local/bin/go-sum-server");
  const [port, setPort] = useState(8725);
  const [domain, setDomain] = useState("go.cybertech.com.vn");
  const [adminEmail, setAdminEmail] = useState("admin@cybertech.com.vn");

  const unitPath = useMemo(() => `/etc/systemd/system/${serviceName}.service`, [serviceName]);
  const siteAvailablePath = useMemo(() => `/etc/nginx/sites-available/${serviceName}`, [serviceName]);

  const systemdUnit = useMemo(
    () => `# ${unitPath}
[Unit]
Description=${description}
After=network-online.target
Wants=network-online.target

[Service]
User=${user}
Group=${group}
WorkingDirectory=${workingDir}
ExecStart=${binaryPath}
Restart=on-failure
RestartSec=2s
KillSignal=SIGINT
TimeoutStopSec=10

[Install]
WantedBy=multi-user.target
`,
    [unitPath, description, user, group, workingDir, binaryPath]
  );

  const enableCmd = useMemo(() => `sudo systemctl enable --now ${serviceName}.service`, [serviceName]);
  const statusCmd = useMemo(() => `sudo systemctl status ${serviceName}`, [serviceName]);

  const nginxConf = useMemo(
    () => `server {
\tlisten 80;
\tserver_name ${domain};

\tlocation / {
\t\tproxy_pass         http://127.0.0.1:${port};
\t\tproxy_http_version 1.1;

\t\tproxy_set_header Host              $host;
\t\tproxy_set_header X-Real-IP         $remote_addr;
\t\tproxy_set_header X-Forwarded-For   $proxy_add_x_forwarded_for;
\t\tproxy_set_header X-Forwarded-Proto $scheme;

\t\tproxy_connect_timeout 5s;
\t\tproxy_send_timeout    60s;
\t\tproxy_read_timeout    60s;
\t}
}
`,
    [domain, port]
  );

  const nginxWriteCmd = useMemo(
    () => `sudo tee ${siteAvailablePath} >/dev/null <<'EOF'
${nginxConf}EOF`,
    [siteAvailablePath, nginxConf]
  );

  const nginxSymlinkCmd = useMemo(
    () => `sudo ln -s ${siteAvailablePath} /etc/nginx/sites-enabled/`,
    [siteAvailablePath]
  );

  const certbotCmd = useMemo(
    () => `sudo certbot --nginx -d ${domain} --redirect -m ${adminEmail} -n --agree-tos`,
    [domain, adminEmail]
  );

  const reloadNginxCmd = `sudo nginx -t && sudo systemctl reload nginx`;

  const scriptAll = useMemo(() => {
    return `#!/usr/bin/env bash
set -euo pipefail

# === Step 1: Write systemd unit ===
cat <<'UNIT' | sudo tee ${unitPath} >/dev/null
${systemdUnit}UNIT

# === Step 2: Enable & start service ===
${enableCmd}

# === Step 3: Check status (optional) ===
${statusCmd} || true

# === Step 4: Write NGINX config ===
${nginxWriteCmd}

# === Step 5: Link sites-available -> sites-enabled ===
${nginxSymlinkCmd}

# === Step 6: Obtain SSL with Certbot ===
${certbotCmd}

# === Step 7: Reload NGINX ===
${reloadNginxCmd}

echo "
✅ Done. Your service '${serviceName}' should be reachable via https://${domain}"
`;
  }, [unitPath, systemdUnit, enableCmd, statusCmd, nginxWriteCmd, nginxSymlinkCmd, certbotCmd, reloadNginxCmd, serviceName, domain]);

  return (
    <div className="page">
      <header className="page-header">
        <div className="container header-inner">
          <div className="brand">
            <span className="brand-logo" aria-hidden>🛠️</span>
            <h1 className="brand-title">Golang Deploy – One-Page Visualizer</h1>
          </div>
          <a href="#steps" className="btn btn-dark">Bắt đầu triển khai</a>
        </div>
      </header>

      <main className="container page-body">
        <section className="grid-2 gap-24">
          <div className="card">
            <h2 className="card-title">Cấu hình đầu vào</h2>
            <p className="muted">Điền thông tin để tự động sinh lệnh &amp; file cấu hình.</p>

            <div className="grid-2 gap-12">
              <Input label="Tên service" value={serviceName} onChange={setServiceName} placeholder="testgo" />
              <Input label="Mô tả (systemd Description)" value={description} onChange={setDescription} placeholder="TestGo Server (port 8725)" />
              <Input label="User (systemd)" value={user} onChange={setUser} placeholder="www-data" />
              <Input label="Group (systemd)" value={group} onChange={setGroup} placeholder="www-data" />
              <Input label="WorkingDirectory" value={workingDir} onChange={setWorkingDir} placeholder="/home/caominh/code/testgo" />
              <Input label="Đường dẫn binary" value={binaryPath} onChange={setBinaryPath} placeholder="/usr/local/bin/go-sum-server" />
              <Input label="Port (ứng dụng Go)" type="number" value={String(port)} onChange={(v) => setPort(Number(v || 0))} placeholder="8725" />
              <Input label="Domain" value={domain} onChange={setDomain} placeholder="go.cybertech.com.vn" />
              <Input label="Email quản trị (SSL)" value={adminEmail} onChange={setAdminEmail} placeholder="admin@cybertech.com.vn" />
            </div>
          </div>

          <div className="card">
            <h2 className="card-title">Checklist nhanh</h2>
            <ul className="list">
              <CheckLine text={`Binary tồn tại tại: ${binaryPath}`} />
              <CheckLine text={`User/Group hợp lệ: ${user}:${group}`} />
              <CheckLine text={`Thư mục làm việc tồn tại: ${workingDir}`} />
              <CheckLine text={`Port backend mở: ${port}`} />
              <CheckLine text={`Domain trỏ về server: ${domain}`} />
            </ul>
            <div className="note">
              Mẹo: Thay <code>www-data</code> bằng <code>caominh</code> nếu bạn muốn chạy bằng user cá nhân.
            </div>
          </div>
        </section>

        <section id="steps" className="stack-24">
          <Step title="Bước 1: Viết file service (systemd)"
                desc={<>Sửa nội dung rồi lưu tại <code className="mono">{unitPath}</code>.</>}>
            <CodeBlock label={`sudo nano ${unitPath}`} code={systemdUnit} lang="ini" />
          </Step>

          <Step title="Bước 2: Enable service" desc="Bật và khởi động dịch vụ ngay.">
            <CodeInline code={enableCmd} />
          </Step>

          <Step title="Bước 3: Kiểm tra status của service" desc="Xem logs & tình trạng (q để thoát).">
            <CodeInline code={statusCmd} />
          </Step>

          <Step title="Bước 4: Viết file cấu hình NGINX cho Go"
                desc={<>Lưu ở <code className="mono">{siteAvailablePath}</code>.</>}>
            <CodeBlock label={`sudo tee ${siteAvailablePath} >/dev/null <<'EOF' ... EOF`} code={nginxConf} lang="nginx" />
          </Step>

          <Step title="Bước 5: Link sites-available → sites-enabled">
            <CodeInline code={nginxSymlinkCmd} />
          </Step>

          <Step title="Bước 6: Cài SSL với Certbot">
            <CodeInline code={certbotCmd} />
          </Step>

          <Step title="Bước 7: Reload NGINX">
            <CodeInline code={reloadNginxCmd} />
          </Step>
        </section>

        <section className="card">
          <h2 className="card-title">Script triển khai nhanh (tổng hợp tất cả bước)</h2>
          <p className="muted">Chạy script dưới đây sau khi bạn đã kiểm tra cấu hình ở phần trên.</p>
          <CodeBlock label="deploy.sh" code={scriptAll} lang="bash" />
          <div className="tiny-muted">Gợi ý: lưu thành <code>deploy.sh</code>, rồi chạy <code>bash deploy.sh</code>.</div>
        </section>

        <footer className="footer">© {new Date().getFullYear()} Deployment Visualizer for Go • Made with ♥</footer>
      </main>
    </div>
  );
}

function Step({ title, children, desc }) {
  return (
    <div className="card">
      <h3 className="card-title">{title}</h3>
      {desc ? <p className="muted">{desc}</p> : null}
      <div className="mt-12">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="field">
      <div className="field-label">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field-input"
      />
    </label>
  );
}

function CodeInline({ code }) {
  return (
    <div className="code-inline">
      <pre className="code-pre"><code>{code}</code></pre>
      <CopyButton text={code} />
    </div>
  );
}

function CodeBlock({ code, label }) {
  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-block-title">{label || "code"}</span>
        <CopyButton text={code} small />
      </div>
      <pre className="code-pre large"><code>{code}</code></pre>
    </div>
  );
}

function CopyButton({ text, small = false }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch (e) {
          console.error(e);
        }
      }}
      className={`btn ${small ? "btn-xs" : "btn-sm"} btn-copy`}
      title="Copy to clipboard"
      type="button"
    >
      {copied ? "Đã copy" : "Copy"}
    </button>
  );
}

function CheckLine({ text }) {
  return (
    <li className="checkline">
      <span className="check">✔</span>
      <span>{text}</span>
    </li>
  );
}
